import { jsxs, jsx } from "react/jsx-runtime";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils.js";
import KatexRenderer from "../../../utils/KatexRenderer.js";
import { formatProblem } from "../../../utils/quiz-parser.js";
import "../shared/quiz-blank.js";
import "../../../node_modules/.pnpm/katex@0.10.2/node_modules/katex/dist/katex.js";
/* empty css                                                                             */
import { QuizResultFeedback } from "../shared/quiz-result-feedback.js";
import { QuizResultToggle } from "../shared/quiz-result-toggle.js";
import { SubmitButton } from "../shared/submit-button.js";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import "../../../context/DevToolsContext.js";
import "../shared/quiz-style-context.js";
import { QuizHeader } from "../shared/quiz-header.js";
import { QuizSolveDescription } from "../shared/quiz-solve-description.js";
import { useExplanationQuiz } from "../hooks/use-explanation-quiz.js";
import { motion } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/render/components/motion/proxy.js";
function ExplanationStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel,
  hideExplanation = false,
  hideSolveDescription = false,
  hideResultFeedback = false,
  answerRevealOnly = false
}) {
  var _a;
  const { t } = useTranslation("quiz");
  const {
    answer,
    setAnswer,
    isSolved,
    canSubmit,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleSubmit
  } = useExplanationQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange,
    showCorrectAnswersInitially: false
  });
  const answerCorrectness = isSolved ? (solveResult == null ? void 0 : solveResult.correct) ?? null : null;
  const isAnswerLocked = isSolved || isViewSolved;
  const correctAnswer = ((_a = solveResult == null ? void 0 : solveResult.correctAnswers) == null ? void 0 : _a.join(", ")) ?? "";
  const displayedAnswer = isSolved && isShowingCorrectAnswers && correctAnswer ? correctAnswer : answer;
  const displayedCorrectness = isShowingCorrectAnswers && correctAnswer ? true : answerCorrectness;
  const renderedProblem = useMemo(() => {
    if (!quiz.problem) return [];
    return formatProblem(
      quiz.problem,
      `${quizId}-problem`,
      () => null,
      quiz.additionalImages ?? void 0
    );
  }, [quiz.problem, quiz.additionalImages, quizId]);
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsx(
      QuizHeader,
      {
        quizId,
        typeLabel: t("types.explanation.label", "서술형"),
        title: quiz.title,
        difficultyLevel,
        variant: "student",
        image: quiz.image,
        imageMobile: quiz.imageMobile,
        desmos: quiz.desmos,
        vars: quiz.vars
      }
    ),
    renderedProblem.length > 0 && /* @__PURE__ */ jsx(
      "div",
      {
        "data-quiz-problem-text": true,
        className: "text-2xl font-medium text-gray-900",
        children: renderedProblem
      }
    ),
    answerRevealOnly ? isSolved && correctAnswer ? /* @__PURE__ */ jsx(
      motion.div,
      {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        role: "status",
        className: "w-full rounded-2xl border-2 border-transparent bg-lc-mint-200 p-4 text-2xl font-medium text-lc-mint-900",
        children: /* @__PURE__ */ jsx(KatexRenderer, { text: correctAnswer })
      }
    ) : null : (
      /* 답변 입력 textarea */
      /* @__PURE__ */ jsxs(
        motion.div,
        {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          transition: { delay: 0.1 },
          children: [
            isAnswerLocked ? /* @__PURE__ */ jsx(
              "div",
              {
                "data-testid": "explanation-answer",
                "aria-invalid": displayedCorrectness === false,
                className: cn(
                  "min-h-[180px] w-full p-4 rounded-2xl border-2 text-2xl font-medium whitespace-pre-wrap break-words",
                  displayedCorrectness === true && "bg-lc-mint-200 text-lc-mint-900 border-transparent",
                  displayedCorrectness === false && "bg-lc-red-200 text-lc-red-900 border-transparent",
                  displayedCorrectness === null && "bg-lc-lightblue-100 text-gray-900 border-lc-lightblue-900"
                ),
                children: /* @__PURE__ */ jsx(KatexRenderer, { text: displayedAnswer })
              }
            ) : /* @__PURE__ */ jsx(
              "textarea",
              {
                value: displayedAnswer,
                onChange: (e) => setAnswer(e.target.value),
                "aria-invalid": displayedCorrectness === false,
                placeholder: t("types.explanation.placeholder", "답변을 입력하세요"),
                maxLength: 500,
                rows: 5,
                className: cn(
                  "w-full p-4 rounded-2xl border-2 text-2xl font-medium text-gray-900 resize-y transition-all outline-hidden",
                  "bg-white border-lc-gray-400 focus:border-lc-lightblue-900 focus:bg-lc-lightblue-200 [box-shadow:0px_4px_0px_rgba(0,0,0,0.1)] focus:[box-shadow:0px_4px_0px_rgba(85,195,255,0.3)]"
                )
              }
            ),
            /* @__PURE__ */ jsxs("div", { className: "mt-1 text-right text-sm text-gray-400", children: [
              displayedAnswer.length,
              "/500"
            ] })
          ]
        }
      )
    ),
    !answerRevealOnly && !hideResultFeedback && /* @__PURE__ */ jsx(
      QuizResultFeedback,
      {
        isCorrect: answerCorrectness,
        show: isSolved && answerCorrectness === null,
        disableSound,
        isViewSolved
      }
    ),
    !answerRevealOnly && !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(
      SubmitButton,
      {
        onClick: handleSubmit,
        disabled: !canSubmit,
        label: submitLabel
      }
    ),
    !answerRevealOnly && isSolved && !hideExplanation && /* @__PURE__ */ jsx(
      QuizResultToggle,
      {
        value: isShowingCorrectAnswers ? "correct-and-explanation" : "my-answer",
        onChange: (next) => setIsShowingCorrectAnswers(next === "correct-and-explanation")
      }
    ),
    answerRevealOnly && isSolved && !hideExplanation && !hideSolveDescription && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: (solveResult == null ? void 0 : solveResult.description) ?? void 0,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: false
      }
    ),
    !answerRevealOnly && isSolved && isShowingCorrectAnswers && !hideExplanation && !hideSolveDescription && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: (solveResult == null ? void 0 : solveResult.description) ?? void 0,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: false
      }
    )
  ] });
}
export {
  ExplanationStudent
};
//# sourceMappingURL=explanation-student.js.map
